const en = {
  demo: {
    recorder_hint: "Start your screen recording now…",
    starting_hint: "Guided demo — starting in 3 seconds",
    cancel: "Cancel",
    exit: "Exit (Esc)",
    pause: "Pause (Space)",
    resume: "Resume (Space)",
    prev: "Previous scene (←)",
    next: "Next scene (→)",
    tts_enable: "Enable voice narration",
    tts_disable: "Disable voice narration",
  },
  common: {
    save: "Save", cancel: "Cancel", delete: "Delete", edit: "Edit",
    create: "Create", add: "Add", search: "Search", loading: "Loading…",
    actions: "Actions", confirm: "Confirm", close: "Close", back: "Back",
    view: "View",
    yes: "Yes", no: "No", all: "All", none: "None", required: "required",
    saved: "Saved", deleted: "Deleted", created: "Created", updated: "Updated",
    download: "Download", import: "Import", export: "Export", upload: "Upload",
    new: "New", filter: "Filter", reset: "Reset",
    copied: "Copied",
  },
  nav: {
    dashboard: "Dashboard", companies: "Companies", contacts: "Contacts",
    principals: "Principals",
    pipeline: "Pipeline", ai_agent: "AI Agent", reports: "Reports", digest: "Digest",
    discover: "Discover",
    approvals: "Approvals",
    email_perf: "Email Perf.",
    ai_coach: "AI Coach",
    quality_audit: "Quality Audit",
    market_intel: "Market Intel",
    api_credits: "API Credits",
    private_docs: "Private Docs",
    missions: "Auto Missions",
    data_prep: "Data (Admin)",
    countries: "Countries", sectors: "Sectors", users: "Users",
    settings: "Settings", import_export: "Import / Export", logout: "Sign out",
    light_mode: "Light mode", dark_mode: "Dark mode", reference: "Reference",
    administration: "Administration",
  },
  auth: {
    welcome_back: "Welcome back",
    login_hint: "Enter your credentials to access your workspace.",
    email: "Email", password: "Password", name: "Name",
    sign_in: "Sign in", signing_in: "Signing in…",
    no_account: "No account?", create_account: "Create account",
    already_account: "Already registered?", sign_up: "Create my account",
    creating: "Creating…",
    tagline_h1: "The prospecting CRM<br />that thinks for you.",
    tagline_p: "Map your markets, identify the right decision-makers and generate personalized pitches. An AI agent does the research work for you.",
    multi_country: "Multi-country", visual_pipeline: "Visual pipeline", ai_agent: "AI Agent",
    live_status: "Live · 2026",
    hero_eyebrow: "AI · Dual pipelines · Multi-country",
    hero_eyebrow_v2: "Powered by Claude Sonnet 4.5",
    tagline_h1_v2: "Prospect <hl>smarter</hl>.<br />Close <hl>faster</hl>.",
    bullet_1: "AI agent scouts companies + decision-makers in real time",
    bullet_2: "Dual pipelines: Mandate (Principals) + Sales (prospects)",
    bullet_3: "Bilingual FR/EN · Multi-country · Principal Portal",
    email_placeholder: "you@company.com",
    mock_pipeline: "Sales Pipeline",
    mock_coach: "Boost open-rate by 18% — switch Tuesday sends to 10am.",
    mock_apply: "Apply",
    mock_ignore: "Ignore",
    mock_deal_won: "Deal won",
    feature_ai_title: "AI Prospecting Agent",
    feature_ai_desc: "Claude Sonnet 4.5 finds the right companies and decision-makers for you.",
    feature_pipeline_title: "Dual pipelines",
    feature_pipeline_desc: "Mandate (your Principals) + Sales (your prospects), in one place.",
    feature_country_title: "Multi-country",
    feature_country_desc: "Track sectors and prospects across 30+ countries.",
    feature_portal_title: "Principal Portal",
    feature_portal_desc: "Magic-link cockpit + monthly PDF reports for your Principals.",
    stat_countries: "countries", stat_pipelines: "pipelines",
    stat_lang: "bilingual", stat_ai: "AI active",
    security_note: "JWT cookies · Multi-tenant · GDPR ready",
    demo_credentials: "",
    split_eyebrow: "AI-augmented prospecting platform",
    split_headline_pre: "Turn your mandates",
    split_headline_em: "into closed deals.",
    split_sub: "An AI agent that maps your markets, qualifies decision-makers and drafts your pitches. Stay focused on what matters: closing.",
    split_feat1: "AI Agent · Claude Sonnet 4.5",
    split_feat2: "Dual pipelines · Mandate + Sales",
    split_feat3: "Multi-tenant · Bilingual FR/EN",
    split_stat1_value: "+87%",
    split_stat1_label: "qualification rate",
    split_stat2_value: "30+",
    split_stat2_label: "countries covered",
    split_stat3_value: "24/7",
    split_stat3_label: "AI agent live",
    footer_legal: "© 2026 Nexaforge",
    terms: "Terms",
    privacy: "Privacy",
    status_operational: "All systems operational",
  },
  dashboard: {
    title: "Dashboard", subtitle: "Overview of your sales activity",
    demo_data: "Demo data", new_company: "New company",
    test_guide: "Test guide (PDF)",
    test_guide_downloaded: "Test guide downloaded",
    pitch_pdf: "Pitch deck PDF",
    pitch_downloaded: "Pitch deck downloaded",
    demo_kit: "Demo Kit",
    demo_kit_label: "Sales & production assets",
    pitch_hint: "8-page client presentation (auto FR/EN)",
    video_kit: "Video Kit (PDF)",
    video_kit_hint: "Script + storyboard + AI tools",
    video_kit_downloaded: "Video kit downloaded",
    auto_demo_script: "Auto-demo script (.py)",
    auto_demo_hint: "Playwright — app plays itself",
    auto_demo_downloaded: "Auto-demo script downloaded",
    guided_demo: "▶ Start guided demo",
    guided_demo_hint: "App navigates itself, in this window",
    demo_data_added: "Demo data added ({{companies}} companies, {{deals}} deals)",
    demo_data_already_present: "Demo data already in place — nothing to add",
    hot_prospects: "Hot Prospects",
    see_all: "See all",
    hot_empty_title: "No hot prospects yet",
    hot_empty_hint: "A company becomes HOT after recent activity (< 7 d) or a deal in proposal/negotiation.",
    hot_reason_stage: "Deal in negotiation",
    hot_reason_recent: "Activity {{n}} d ago",
    hot_reason_new: "New signal",
    hot_leads: "Hot leads to follow up",
    hot_leads_empty_title: "No hot leads yet",
    hot_leads_empty_hint: "A contact becomes hot after opens, clicks or replies to your emails.",
    hot_leads_empty_no_signed: "Mark a Principal as 'signed' in Principals first.",
    for: "for",
    empty_title: "Get started with demo data",
    empty_subtitle: "5 countries, 6 sectors, 8 companies and pre-configured deals.",
    load_demo: "Load demo",
    kpi_companies: "Companies", kpi_contacts: "Contacts", kpi_active_deals: "Active deals",
    kpi_pipeline_value: "{{value}} € pipeline", kpi_conversion: "Conversion rate",
    pipeline_evolution: "Pipeline evolution (90 days)",
    by_sector: "By sector", top_companies_ai: "Top companies (AI Score)",
    no_scored: "No scored companies yet. Use \"Enrich with AI\" on a company page.",
    top_deals: "Top deals", no_deals: "No deals yet",
    countries_title: "Countries", no_data: "No data",
    data_health_title: "Data Health",
    data_health_breakdown: "{{verified}}% verified contacts · {{dm}}% companies with decision-maker",
    data_health_tier_excellent: "Excellent",
    data_health_tier_good: "Good",
    data_health_tier_fair: "Fair",
    data_health_tier_poor: "Poor",
  },
  coverage: {
    heat_map_title: "Coverage Heat Map",
    target_per_cell: "Target: {{n}} contacts / cell",
    objectives_btn: "Objectives",
    loading: "Loading…",
    no_data: "No coverage data yet.",
    no_data_hint: "Define your objectives per Mandant to reveal target areas.",
    set_objectives: "Define my objectives",
    cell_hint: "Click on a cell to see details and launch a targeted mission.",
    mandant: "Mandant",
    total: "Total",
    contacts: "Contacts",
    verified_emails: "Verified emails",
    decision_makers: "Decision makers",
    drill_description: "Coverage detail for this Mandant × Country combination.",
  },
  countries: {
    title: "Countries", subtitle: "Map your geographic markets",
    new: "New country", edit: "Edit country", add: "Add country",
    name: "Name", code: "ISO Code", flag: "Flag (emoji)", currency: "Currency",
    col_country: "Country", col_companies: "Companies", col_deals: "Active deals", col_pipeline: "Pipeline",
    empty: "No countries. Add one to get started.",
    confirm_delete: "Delete this country?",
  },
  sectors: {
    title: "Sectors", subtitle: "Categorize your target industries (parents → sub-sectors)",
    new: "New sector", edit: "Edit sector", add: "Add sector",
    add_subsector: "Add sub-sector",
    add_subsector_short: "Sub-sector",
    parent_label: "Parent sector",
    name: "Name", description: "Description", icon: "Icon", color: "Color",
    name_required: "Name is required",
    empty: "No sectors. Add one to get started.",
    confirm_delete: "Delete this sector?",
    companies_attached: "Attached companies",
    subsectors: "sub-sectors",
    no_subsectors: "No sub-sectors. Click \"Sub-sector\" or \"AI Suggest\" to add some.",
    suggest_ai: "AI Suggest",
    suggest_ai_hint: "Ask AI for relevant sub-sector suggestions",
    suggest_title: "AI suggestions for \"{{name}}\"",
    suggesting: "AI is thinking of the best sub-sectors…",
    no_suggestions: "No suggestions. Retry or add one manually.",
    no_suggestion_selected: "Select at least one suggestion.",
    add_selected: "Add ({{n}})",
    subsectors_added: "{{count}} sub-sectors added",
  },
  companies: {
    title: "Companies", subtitle_count: "{{count}} company",
    subtitle_count_plural: "{{count}} companies",
    new: "New company", add: "Add company",
    add_hint: "Fill in the name, country, sector and role of the company.",
    name: "Name", country: "Country", sector: "Sector", website: "Website",
    linkedin: "LinkedIn URL", size: "Size",
    search_placeholder: "Search by name…",
    filter_all_countries: "All countries", filter_all_sectors: "All sectors",
    filter_all_sizes: "All sizes",
    col_company: "Company", col_size: "Size", col_score: "AI Score",
    col_contacts: "Contacts", col_deals: "Deals", col_status: "Status",
    status_enriched: "Enriched", status_in_progress: "In progress", status_pending: "Pending",
    none: "No companies found",
    tag_ai_discovered_hint: "Found by AI discovery",
    role_all: "All",
    linked_principals: "Linked Principal(s)",
    linked_principals_placeholder: "Select one or more Principals…",
    linked_principals_hint: "This Prospect is targeted on behalf of these Principals.",
    linked_principals_empty: "No linked Principal yet.",
  },
  role: {
    label: "Role",
    principal: "Principal",
    prospect: "Prospect",
    other: "Other",
    hint_principal: "Company you represent — you sell its products/services to Prospects.",
    hint_prospect: "End-customer target — your sales target on behalf of one or more Principals.",
    hint_other: "Partner, supplier, network contact, competitor — outside Principal/Prospect model.",
    principal_status: {
      prospecting: "Prospecting",
      in_discussion: "In discussion",
      proposal_sent: "Proposal sent",
      signed: "Signed",
      declined: "Declined",
      paused: "Paused",
    },
  },
  principals: {
    title: "Principals",
    subtitle_count: "{{count}} principal",
    subtitle_count_plural: "{{count}} principals",
    new: "New Principal",
    add: "Add Principal",
    status: "Mandate status",
    products_services: "Products & services offered",
    products_services_placeholder: "e.g. CRM Software, 5-day Training…",
    products_services_hint: "Type and press Enter to add each offering you commercialize for this Principal.",
    search_placeholder: "Search a Principal…",
    filter_all_status: "All statuses",
    stats: {
      summary: "Summary",
      in_progress: "In progress (discussion + proposal)",
      total: "Displayed total",
      tenant_total: "tenant total",
      by_status: "By status",
      by_sector: "Top sectors",
    },
    linked_prospects: "Linked prospects",
    find_prospects_btn: "Find prospects",
    analyze_dna_btn: "Analyze website (DNA)",
    empty_title: "No Principals yet",
    empty_hint: "A Principal is a company you represent. You sell their products/services to your Prospects.",
    required_fields: "Name, country and sector are required",
    created: "Principal created",
  },
  prospects: {
    title: "Find prospects for {{principal}}",
    target_markets: "Saved target markets",
    target_countries: "Target countries",
    target_sectors: "Target sectors",
    search_in_country: "Search in country",
    in_sector: "In sector",
    any_sector: "Any sector",
    find: "Start search",
    searching: "Searching…",
    pick_country: "Choose a country",
    existing_in_db_title: "Already in DB ({{count}})",
    no_existing: "No company in the same sector found in this country in your DB yet.",
    new_candidates_title: "New web candidates ({{count}})",
    no_new_candidates: "No new candidate found.",
    tavily_hint: "Configure Tavily in Settings to enable web discovery of new companies.",
    already_linked: "Linked",
    select_all: "Select all",
    review_warning: "Review each candidate — Tavily can return articles or directories that aren't real companies. Only check items matching an actual company website.",
    quality_medium: "Review",
    quality_medium_hint: "Non-root URL — verify it's actually a company website.",
    confirm_llm_title: "Confirm via Claude (recommended)",
    confirm_llm_hint: "A 1-sentence AI check rules out residual false positives.",
    llm_validated: "AI-Validated {{confidence}}%",
    apply: "Link / Import ({{count}})",
    applying: "Applying…",
    applied: "✅ {{linked}} companies linked · {{created}} new imported",
  },
  principal_360: {
    title: "360° View",
    subtitle: "Principal cockpit",
    prospects_count: "Targeted prospects",
    contacts: "contacts",
    dms: "DMs",
    pipeline_value: "Open pipeline",
    open_deals: "open deals",
    won_value: "Won (€)",
    won_deals: "won",
    lost_deals: "lost",
    emails_sent: "Emails sent",
    top_prospects: "Top 5 Prospects (by AI score)",
    no_prospects_yet: "No Prospect linked to this Principal yet.",
    recent_activity: "Recent activity (30d)",
    no_activity: "No recent activity.",
    commercializing: "You commercialize for this Principal",
    export_pdf: "Export PDF",
    pdf_ready: "PDF report generated",
  },
  principal_digest: {
    title: "Monthly Principal Digest",
    subtitle: "Automatically email the 360° PDF report to this Principal on the 1st of each month.",
    enabled_label: "Enable monthly auto-send",
    viewer_email: "Principal email",
    send_test: "Send test",
    hint: "The PDF is attached and sent from your verified domain. 1st of each month at 8:00 UTC.",
    sent_to: "Sent to",
    recent_sends: "Recent sends",
    email_required: "Please set the Principal's email first",
  },
  portal_invite: {
    title: "Principal Portal (read-only)",
    button: "Invite to portal",
    hint: "Sends a 24h magic link giving access to live cockpit + PDF archives.",
    lang_hint: "Invitation email and reports are sent in the selected language (FR/EN).",
    sent: "Invitation sent",
    last_login: "last login",
    never_connected: "never logged in",
  },
  portal: {
    brand: "Principal Portal",
    loading: "Loading…",
    welcome: "Signed in as {{name}}",
    logout: "Log out",
    nav: {
      cockpit: "Cockpit",
      prospects: "Prospects",
      activities: "Activities",
      digests: "PDF Archive",
    },
    login: {
      verifying: "Verifying your invitation…",
      success: "Access granted, redirecting…",
      no_token: "Invalid invitation link.",
      help: "Ask your sales rep to resend a fresh invitation.",
    },
    cockpit: {
      eyebrow: "LIVE COCKPIT",
      last_update: "Last refresh:",
      products: "Products & services commercialized for you",
      top_prospects: "Top targeted prospects (by AI score)",
      no_prospects: "No prospect linked yet.",
      recent_activity: "Recent activity (30d)",
      no_activity: "No recent activity.",
      kpi: {
        prospects: "Targeted prospects",
        contacts: "contacts",
        dms: "decision makers",
        pipeline: "Open pipeline",
        open_deals: "open deals",
        won: "Confirmed wins (€)",
        emails: "Emails sent",
      },
    },
    prospects: {
      title: "Prospects targeted for you",
      subtitle: "prospect(s) linked to your mandate",
      empty: "No prospect linked yet. Your sales rep is on it.",
    },
    activities: {
      title: "Activity timeline",
      subtitle: "Last 30 days of sales actions performed for you.",
      empty: "No recent activity.",
    },
    digests: {
      title: "Monthly report archive",
      subtitle: "Your past monthly PDF reports.",
      empty: "No report sent yet.",
      failed: "Send failed",
      download: "Download",
    },
    pulse: {
      title: "Weekly pulse",
      last_7d: "· last 7 days",
      active: "Active agent",
      new_prospects: "New prospects",
      deals_advanced: "Deals advanced",
      emails_sent: "Emails sent",
      activities: "Activities",
      reassure_active: "Your sales agent has been active on your mandate this week.",
      reassure_quiet: "Quiet week. Your sales agent will resume activity shortly.",
    },
    notif: {
      title: "Notifications",
      empty: "No notifications yet.",
      mark_all: "Mark all read",
    },
  },
  ai_coach: {
    title: "AI Coach",
    analyze_now: "Analyze",
    last_run: "Last analysis",
    auto: "auto",
    manual: "manual",
    empty: "No active recommendations.",
    empty_hint: "Click 'Analyze' to generate prescriptions from your email performance.",
    sparse_msg: "Insufficient data ({{count}} emails, min {{min}}). Send more to activate the coach.",
    run_done: "{{count}} recommendation(s) generated",
    apply: "Apply",
    ignore: "Ignore",
    applied: "Marked as applied",
    ignored: "Recommendation ignored",
    confidence: "confidence",
  },
  ai_coach_history: {
    title: "AI Coach — History & impact",
    subtitle: "Track past recommendations and measure impact on your email performance.",
    refresh: "Refresh",
    kpi: {
      total: "Total",
      total_hint: "last 90 days",
      applied: "Applied",
      ignored: "Ignored",
      pending: "Pending",
    },
    impact: {
      title: "Email performance over time",
      subtitle: "last 12 weeks",
      open: "Open rate",
      click: "Click rate",
      reply: "Reply rate",
      empty: "No email data for this period.",
      empty_hint: "Send emails and performance will show up here.",
      milestones: "Recent applied recommendations",
    },
    list: {
      title: "Recommendations history",
      empty: "No recommendations for this filter.",
    },
    filters: {
      all: "All",
      new: "Pending",
      applied: "Applied",
      ignored: "Ignored",
      superseded: "Superseded",
    },
    applied_on: "applied on",
    ignored_on: "ignored on",
    reapply: "Reactivate",
    reapplied: "Recommendation reactivated",
  },
  company_detail: {
    overview: "Overview", contacts: "Contacts", offerings: "Products & Services",
    competitors: "Competitors", ai: "AI Analysis", deals: "Deals",
    activities: "Activities", synergy: "Synergy",
    description: "Description", information: "Information",
    enrich_ai: "Enrich with AI", enriching: "Enriching…",
    find_decision_makers: "Find decision makers", searching: "Searching…",
    find_contacts_btn: "Find contacts",
    find_contacts_mode: "Search type",
    mode_dm: "Decision-makers",
    mode_all: "All contacts",
    validate_via_claude: "Validate via Claude",
    validate_via_claude_hint: "Rules out suspicious names/emails returned by Hunter.io.",
    run_search: "Run search",
    competitor_ai: "Run AI analysis", analyzing: "Analyzing…",
    generate_pitch: "Generate pitch", generating: "Generating…",
    approach_angles: "Sales approach angles",
    pitches_generated: "Pitches generated",
    no_description: "No description. Run \"Enrich with AI\" to generate a complete analysis.",
    pain_points: "Pain points", strengths: "Strengths", weaknesses: "Weaknesses",
    opportunities: "Opportunities",
    add_note_placeholder: "Add a quick note…",
    confirm_delete_company: "Permanently delete \"{{name}}\" and all related data?",
    no_activity: "No activity",
    sources_label: "Web sources (Tavily)",
    no_reports: "No AI report. Click \"Enrich with AI\" to start.",
    no_competitors_ai: "Run AI analysis to discover competitors and differentiation angles.",
    no_product: "No products detected", no_service: "No services detected",
    no_contact: "No contacts",
    formal_email: "Formal email", linkedin_short: "Short LinkedIn", punchy_email: "Punchy email",
    subject: "Subject", copy: "Copy", copied: "Copied",
    decision_maker: "Decision-maker",
    dm_no_verified_title: "No verified decision-maker found",
    dm_no_verified_hint: "We don't make up contacts. Use the LinkedIn links below to manually find decision-makers, then add them via 'Add contact'.",
    dm_add_manually_hint: "Configure Apollo.io or Hunter.io in Settings to automate verified decision-maker discovery.",
    not_configured: "Not configured",
  },
  company: {
    dm_added: "{{count}} decision-makers added (source: {{source}})",
    dm_added_v2: "{{count}} contacts added ({{llm}} AI-validated) · source: {{source}}",
    dm_no_verified: "No verified decision-maker found — manual search required",
  },
  contacts: {
    title: "Contacts", count: "{{count}} contact",
    new: "Add", add: "Add contact",
    first_name: "First name", last_name: "Last name", position: "Position",
    email: "Email", phone: "Phone", seniority: "Seniority",
    decision_maker: "Decision-maker", source: "Source",
    manual: "Manual",
    unverified: "Unverified",
    search_placeholder: "Search first name, last name, email…",
    filter_all_seniorities: "All seniorities", filter_decision_makers: "Decision-makers only",
    filter_all: "All",
    col_contact: "Contact", col_company: "Company", col_position: "Position",
    col_contact_info: "Contact info", col_seniority: "Seniority", col_source: "Source",
    empty: "No contacts",
    verified: "Verified",
    send: "Send", call: "Call", open: "Open", close: "Close",
    open_company: "Open company",
    save_notes: "Save notes",
    notes: "Notes",
    notes_placeholder: "Internal notes about this contact…",
    tab_info: "Information",
    tab_engagement: "Engagement",
    emails_sent: "Sent",
    emails_opened_kpi: "Opened",
    emails_clicked_kpi: "Clicked",
    emails_replied_kpi: "Replied",
    emails_bounced_kpi: "Bounced",
    deals: "Deals",
    last_engagement: "Last activity",
    no_engagement: "No engagement history yet for this contact.",
    recent_emails: "Recent emails",
    linked_deals: "Linked deals",
    recent_activities: "Recent activities",
    loading: "Loading…",
    opened: "Opened",
    clicked: "Clicked",
    replied: "Replied",
    bounced: "Bounced",
    sort_recent: "Most recent",
    sort_score: "Hottest first",
    tier_hot: "Hot",
    tier_warm: "Warm",
    tier_tepid: "Tepid",
    tier_cold: "Cold",
    score_explain: "Engagement Score (90-day window)",
    opens: "Opens",
    clicks: "Clicks",
    replies: "Replies",
    bounces: "Bounces",
    deals_30d: "Deals 30d",
    activities_30d: "Activities 30d",
    multinational_risk: "Multinational domain – verify",
    entity_verified: "Entity verified",
    verify_via_linkedin: "Verify via LinkedIn",
  },
  pipeline: {
    title: "Pipeline", subtitle: "Kanban view of your sales opportunities (Prospects)",
    subtitle_mandate: "Kanban view of your Principal signing journey",
    tab_sales: "Sales Pipeline",
    tab_mandate: "Mandate Pipeline",
    new_deal: "New deal", create_deal: "Create deal", pdf_ready: "Pipeline PDF generated",
    deal_title: "Title", company: "Company", value: "Value",
    currency: "Currency", stage: "Stage", close_date: "Expected close date",
    notes: "Notes",
    stat_active: "Active deals", stat_value: "Pipeline value",
    stat_won: "Won", stat_conversion: "Conversion",
    stages: {
      LEAD: "Lead", QUALIFIED: "Qualified", CONTACTED: "Contacted",
      MEETING_SCHEDULED: "Meeting", PROPOSAL_SENT: "Proposal",
      NEGOTIATION: "Negotiation", WON: "Won", LOST: "Lost",
      IDENTIFIED: "Identified", IN_DISCUSSION: "In discussion",
      SIGNED: "Signed", DECLINED: "Declined",
    },
  },
  ai_agent: {
    title: "AI Prospecting Agent",
    subtitle: "Claude Sonnet 4.5 · fallback GPT-5.2",
    new_conv: "New conversation", history: "History",
    no_conv: "No conversation",
    welcome: "What are you looking for today?",
    welcome_sub: "Ask the agent to find companies, qualify prospects, or suggest decision-makers.",
    placeholder: "Type your request… (Enter to send, Shift+Enter for new line)",
    thinking: "The agent is thinking…",
    confirm_delete_session: "Delete this conversation?",
  },
  reports: {
    title: "AI Reports", subtitle: "All analyses generated by the agent",
    empty: "No report yet. Enrich a company to generate an AI analysis.",
  },
  settings: {
    title: "Settings", subtitle: "Configure your profile and the AI agent",
    profile: "Profile",
    activity_section: "Your activity (used by the AI agent)",
    activity_label: "Description of your activity",
    activity_hint: "The AI uses this text to generate personalized pitches and contextualize its answers.",
    services_label: "Services you offer",
    add_service: "Add a service…",
    preferences: "Preferences",
    default_currency: "Default currency", ai_language: "AI language",
    integrations: "External integrations",
    integrations_hint: "Enter your API keys to enable real-data enrichment. Keys are stored encrypted and masked when displayed.",
    apollo_key: "Apollo.io key", hunter_key: "Hunter.io key",
    tavily_key: "Tavily key", key_placeholder: "Paste your key…",
    where_to_get: "Where to get it?",
    save_settings: "Save",
  },
  users: {
    title: "Users", subtitle: "Manage accounts and their access rights",
    new: "New account", create: "Create user", edit: "Edit user",
    email: "Email", password: "Password", name: "Name", role: "Role",
    role_admin: "Admin (full access + management)",
    role_user: "User (can edit, limited scope)",
    role_viewer: "Viewer (read-only, limited scope)",
    access_all: "Full access to all data",
    access_scope: "Limit access",
    access_countries: "Accessible countries",
    access_sectors: "Accessible sectors",
    access_companies: "Specific companies",
    is_active: "Account active",
    col_user: "User", col_role: "Role", col_access: "Scope",
    col_status: "Status", col_created: "Created", col_digest: "Digest",
    digest_enabled: "Weekly digest (email)",
    digest_hint: "User will receive a Monday-morning weekly recap.",
    digest_enabled_toast: "Digest enabled for {{email}}",
    digest_disabled_toast: "Digest disabled for {{email}}",
    digest_test_sent: "Test email sent to {{email}}",
    digest_send_test: "Send a test email",
    confirm_delete: "Delete this account?",
    cannot_delete_self: "You cannot delete your own account",
  },
  import_export: {
    title: "Import / Export",
    subtitle: "Bulk-load data from a CSV file or export your database.",
    templates: "File templates",
    templates_hint: "Download a pre-filled template, fill in your data, then import the file.",
    template_companies: "Companies template (CSV)",
    template_contacts: "Contacts template (CSV)",
    download_template: "Download template",
    import: "Import a file",
    import_companies: "Import companies",
    import_contacts: "Import contacts",
    choose_file: "Choose a CSV file",
    drop_or_click: "Drop your file here or click to browse",
    importing: "Importing…",
    export: "Export database",
    export_companies: "Export all companies",
    export_contacts: "Export all contacts",
    export_deals: "Export all deals",
    export_activities: "Export all activities",
    export_filters: "Advanced filters",
    export_filters_hint: "Filter your CSV exports by date, country, sector, stage and more.",
    filter_date_from: "Date from (created after)",
    filter_date_to: "Date to (created before)",
    filter_close_from: "Expected close — from",
    filter_close_to: "Expected close — to",
    filter_countries: "Countries",
    filter_sectors: "Sectors",
    filter_stages: "Stages",
    filter_sizes: "Company sizes",
    filter_seniorities: "Seniorities",
    filter_owners: "Owners",
    filter_activity_types: "Activity types",
    filter_ai_score_min: "Min AI score",
    filter_ai_score_max: "Max AI score",
    filter_value_min: "Min value (€)",
    filter_value_max: "Max value (€)",
    filter_dm_only: "Decision-makers only",
    filter_tag: "Tag (single)",
    filter_select_placeholder: "—",
    filter_multi_placeholder: "Select…",
    filter_reset: "Reset",
    filter_export_now: "Export",
    no_filters: "No filters — exporting everything.",
    active_filters: "{{count}} active filter",
    active_filters_plural: "{{count}} active filters",
    result_title: "Import result",
    result_created: "{{n}} created", result_updated: "{{n}} updated",
    result_skipped: "{{n}} skipped",
    errors_title: "Skipped lines",
    error_line: "Line {{line}}",
    notes_title: "How does it work?",
    notes: [
      "Companies must reference an existing country via country_code (e.g., FR, MA).",
      "If the sector does not exist, it will be created automatically.",
      "For contacts, company_name must match an existing company.",
      "Import is idempotent: a duplicate (same name) will be updated, not duplicated.",
    ],
  },
  synergy: {
    title: "Synergy", legend: "Full company overview",
    timeline: "Activity feed", radial: "Radial view", related: "Related companies",
    no_timeline: "No event recorded for this company yet.",
    no_related: "No related companies (same sector or country).",
    same_sector: "Same sector", same_country: "Same country",
    decision_makers: "Decision-makers", active_pipeline: "Active pipeline",
    products: "Products", services: "Services",
    competitors: "Competitors", reports: "AI reports",
    click_hint: "Click any node to see the details",
    details_for: "For {{name}}",
    empty_list: "Nothing to show.",
    decision_maker: "Decision-maker",
    // Explorer mode (cross-companies)
    explorer_mode: "Explorer",
    scope_this: "This company",
    scope_same_sector: "Same sector",
    scope_same_country: "Same country",
    scope_same_sector_and_country: "Sector + Country",
    explorer_for: "For {{name}} · scope: {{scope}}",
    peers_count_one: "{{count}} peer company",
    peers_count_other: "{{count}} peer companies",
    peers_count: "{{count}} peers",
    empty_explorer: "Nothing matches scope \"{{scope}}\".",
    decision_makers_only: "Decision-makers only",
    via: "via",
  },
  timeline: {
    company_created: "Company added to database",
    deal_created: "Deal created: {{deal}}",
    deal_updated: "Deal updated: {{deal}}",
  },
  errors: {
    network: "Network error", forbidden: "Action not allowed",
    not_found: "Item not found", validation: "Invalid data",
  },
  digest: {
    title: "Weekly digest",
    subtitle: "Automatic recap of AI signals, deal movements and decision-makers from the past 7 days.",
    preview_email: "Preview email",
    preview_label: "HTML preview",
    send_test_to_me: "Send me a test",
    run_now: "Send now to everyone",
    not_configured: "Resend is not configured. Add RESEND_API_KEY in the backend to enable sending.",
    schedule: "Schedule",
    schedule_hint: "The digest is sent automatically at this time each week.",
    my_status: "My subscription",
    enabled: "Enabled",
    disabled: "Disabled",
    ask_admin: "Ask your admin to enable",
    self_disabled: "Your digest is disabled. Only an admin can re-enable it.",
    history_count: "Digests sent",
    last_sends: "Rows stored",
    current_week: "This week's preview",
    kpi_pipeline: "Pipeline", kpi_active: "Active deals",
    kpi_wins: "Won (7d)", kpi_new: "New deals",
    hot_signals: "🔥 Hot AI signals",
    advanced_deals: "📈 Deal movement",
    new_dms: "👤 New decision-makers",
    stagnant: "⏰ Stagnant deals (>14d)",
    no_hot: "No hot signal this week.",
    no_movement: "No deal movement.",
    no_dms: "No new decision-makers.",
    no_stagnant: "All your deals are fresh.",
    history: "History",
    empty_history: "No digest sent yet. Click 'Send me a test'.",
    col_sent_at: "Sent at", col_user: "User",
    col_subject: "Subject", col_status: "Status",
    sent: "Sent", failed: "Failed",
    test_sent: "Email sent to {{email}}",
    run_done: "{{sent}}/{{total}} digests sent",
    day_mon: "Monday", day_tue: "Tuesday", day_wed: "Wednesday",
    day_thu: "Thursday", day_fri: "Friday", day_sat: "Saturday", day_sun: "Sunday",
  },
  discover: {
    title: "Discover companies",
    subtitle: "The AI agent searches the web and suggests relevant prospects you can add with one click.",
    ai_approved_banner: "Each candidate is extracted from the web (Tavily) then curated by Claude (Anthropic). The fit score (0-100) reflects AI-assessed relevance against your services + criteria. Candidates ≥ 70 are tagged « AI-Approved ».",
    ai_approved: "AI-Approved",
    form_title: "Search criteria",
    field_countries: "Target countries",
    field_sectors: "Target sectors",
    field_services: "My services offered",
    services_hint: "Pre-filled from your settings. Edit for this search only.",
    field_criteria: "Additional criteria (optional)",
    field_limit: "Number of companies",
    placeholder_countries: "All countries",
    placeholder_sectors: "All sectors",
    placeholder_services: "e.g. SaaS CRM for SMBs",
    placeholder_criteria: "e.g. Series A/B startups, 50-200 employees, e-commerce growth stage…",
    companies: "companies",
    need_tavily: "Tavily API is not configured. Add your key in Settings → API Keys to use discovery.",
    need_filter: "Provide at least a country, a sector, or additional criteria.",
    run: "Run discovery",
    searching: "Searching…",
    found: "{{n}} prospects identified",
    saved: "{{created}} added · {{skipped}} already existed",
    sources_used: "Web sources used by the AI",
    selected_count: "{{count}}/{{total}} selected",
    select_all: "Select all",
    deselect_all: "Deselect all",
    rerun: "Run again",
    add_selected: "Add {{count}} company",
    add_selected_plural: "Add {{count}} companies",
    assign_country: "Country",
    assign_sector: "Sector",
    unassigned: "Unassigned",
    no_candidates: "No prospect identified. Refine your criteria.",
  },
  notifications: {
    title: "Notifications",
    empty: "No notifications.",
    mark_all_read: "Mark all as read",
    view_all: "View all",
    just_now: "just now",
    minutes_ago: "{{n}} min ago",
    hours_ago: "{{n}} h ago",
    days_ago: "{{n}} d ago",
    prefs_title: "Notification preferences",
    prefs_hint: "Choose what you want to receive and how.",
    event_hot_ai_signal: "Hot AI signal on a watched company",
    event_deal_stage_change: "Deal stage change",
    event_new_decision_maker: "New decision-maker added",
    event_stagnant_deal: "Stagnant deal (>14 days)",
    col_in_app: "In-app",
    col_email: "Email",
    email_not_configured: "Resend not configured — emails will not be sent.",
  },
  watch: {
    title: "Automated watch",
    subtitle: "Daily AI monitoring of news on your top-priority companies.",
    last_run: "Last run",
    never: "Never",
    schedule: "Schedule: every day at {{hour}}:00 UTC",
    run_now: "Run now for me",
    no_signals: "No signal detected yet.",
    signals_count: "{{n}} recent signals",
    hot_only: "Hot signals only",
    detected_at: "Detected at",
    open_company: "Open company",
  },
  temperature: {
    HOT: "Hot", WARM: "Warm", COLD: "Cold",
    hot_short: "🔥", warm_short: "☀️", cold_short: "❄️",
    title: "Temperature",
    filter_all: "All",
    filter_hot: "🔥 Hot", filter_warm: "☀️ Warm", filter_cold: "❄️ Cold",
  },
  pdf: {
    export_one: "Export as PDF",
    export_bulk: "Export all reports",
    generating: "Generating PDF…",
  },
  autopilot: {
    title: "Pipeline Autopilot",
    subtitle: "Let AI discover, enrich and qualify prospects automatically every day.",
    active: "Active",
    enable: "Enable Autopilot",
    enable_hint: "Runs daily at {{hour}}:00 UTC in background. You get a notification as soon as new prospects are ready.",
    mode: "Prospecting mode",
    mode_find_customers: "Find Customers",
    mode_find_customers_hint: "Find Prospects for each Principal (uses their products/services as pitch)",
    mode_find_principals: "Find Principals",
    mode_find_principals_hint: "Find companies you could represent as an agent",
    mode_both: "Both",
    mode_both_hint: "Principals + Customers each run",
    countries: "Target countries",
    sectors: "Target sectors",
    any: "Any",
    criteria: "Additional criteria",
    criteria_placeholder: "e.g. Series A/B, 50-200 employees, B2B SaaS…",
    min_score: "Min AI score",
    max_per_run: "Max per run",
    actions_per_run: "Actions to chain",
    auto_enrich: "Auto-enrich (AI analysis + sources)",
    auto_find_dms: "Find decision-makers (Apollo + AI fallback)",
    last_runs: "Last runs",
    created_label: "added",
    enriched_label: "enriched",
    run_now: "Run now",
    saved: "Configuration saved",
    skipped: "Skipped: {{reason}}",
    error: "Error",
    run_done: "{{created}} prospects added, {{enriched}} enriched",
    running_bg: "Autopilot started in background. Result in ~30s.",
  },
  email: {
    compose_title: "AI Email Composer",
    no_contact_selected: "No contact selected",
    no_recipient: "No email for this contact",
    tone: "Tone",
    tone_formal: "Formal",
    tone_casual: "Casual",
    tone_urgent: "Direct & urgent",
    include_signals: "Include recent web signals",
    include_signals_hint: "Uses latest Tavily news (funding, hiring, IPO…) to personalise.",
    custom_brief: "Extra brief (optional)",
    custom_brief_placeholder: "e.g. mention our recent retail case study",
    generate: "Generate draft",
    regenerate: "Regenerate",
    generating: "Generating…",
    draft_ready: "Draft ready — review before sending",
    subject: "Subject",
    body: "Email body",
    preview: "Preview",
    edit: "Edit",
    send: "Send",
    sending: "Sending…",
    sent_to: "Email sent to {{email}}",
    signals_used: "Web signals used",
    history_title: "Sent emails",
    validating: "Validating…",
    validation_valid: "Valid email",
    validation_risky: "Risky",
    validation_invalid: "Invalid email",
    validation_unknown: "Unverified",
    blocked_title: "Send blocked — invalid address",
    blocked_hint: "This address failed automatic validation. Fix the contact email or disable validation in Settings.",
    blocked_invalid: "Send blocked — invalid address ({{flags}})",
    followup_label: "Schedule auto follow-up",
    followup_hint: "AI will draft the follow-up and queue it for your approval — zero auto-sending.",
    followup_days_unit: "day(s)",
    fu_scheduled: "Follow-up scheduled",
    fu_sent: "Follow-up sent",
    fu_drafted_pending: "Draft awaiting approval",
    fu_cancelled: "Follow-up cancelled",
    fu_skipped_opened: "Email opened — follow-up skipped",
    fu_skipped_bounced: "Bounced — follow-up skipped",
    fu_skipped: "Follow-up skipped",
    fu_error: "Follow-up error",
    fu_due_at: "due {{date}}",
    fu_send_now: "Send now",
    fu_cancelled_ok: "Follow-up cancelled",
    fu_sent_ok: "Follow-up sent",
  },
  approvals: {
    title: "Email Approvals",
    subtitle: "Review and approve every AI-generated follow-up before it goes out.",
    queue: "Queue",
    empty_title: "No pending approvals",
    empty_hint: "When the AI prepares a follow-up, it lands here for your review before sending.",
    select_hint: "Pick a follow-up from the list to review it.",
    approve_send: "Approve & send",
    reject: "Reject",
    save_edit: "Save",
    edit_saved: "Edits saved",
    approved_ok: "Email sent",
    rejected_ok: "Follow-up rejected",
    confirm_reject: "Permanently reject this follow-up?",
    confirm_approve: "Send this email to {{email}} now?",
    expires_on: "Expires on {{date}}",
  },
  email_perf: {
    title: "Email Performance",
    subtitle: "Track engagement: opens, clicks, best send hours, winning subjects.",
    sent: "Sent",
    delivered: "Delivered",
    delivered_n: "delivered",
    open_rate: "Open rate",
    click_rate: "Click rate",
    bounce_rate: "Bounce rate",
    opens: "opens",
    clicks: "clicks",
    bounces: "bounces",
    over_days: "over {{d}} days",
    timeline: "Timeline",
    heatmap: "Best send hours (open rate)",
    heatmap_hint: "The brighter the color, the better the open rate at that slot.",
    top_subjects: "Top subjects (open rate)",
    top_companies: "Most engaged companies",
    reply_rate_soon_title: "Reply rate — coming soon",
    reply_rate_soon_hint: "Requires Gmail/Outlook integration to auto-detect incoming replies.",
    coming_soon: "Soon",
  },
  validation: {
    title: "Email validation",
    description: "Anti-bounce pipeline: format → MX DNS (free, unlimited) → NeverBounce (premium, for risky cases).",
    active: "Active",
    not_configured: "Not configured",
    low_balance: "Low balance",
    free_credits: "Free",
    paid_credits: "Paid",
    total_credits: "Total",
    pipeline_hint: "Emails already invalid at format/MX stage consume ZERO NeverBounce credits.",
    skip_label: "Disable pre-send validation",
    skip_hint: "Only enable if you're 100% sure of your list quality. Increased Resend ban risk.",
  },
  dataprep: {
    title: "Data preparation",
    subtitle: "Back up your data, then wipe the demo database before Phase 2.",
    step1_title: "Step 1 — Back up your current data",
    step1_hint: "Download a ZIP with one CSV per collection (companies, contacts, deals, emails, …). Keep it safe.",
    business_data: "Business data (will be wiped)",
    kept_data: "Kept data",
    kept_hint: "Your users, passwords, sectors and countries reference data stay intact.",
    download_zip: "Download full ZIP archive",
    exporting: "Generating archive…",
    export_ok: "Archive downloaded — keep it safe",
    export_ok_suffix: "downloaded",
    export_fail: "Export failed",
    step2_title: "Step 2 — Wipe business data (irreversible)",
    step2_hint: "This removes all companies, contacts, deals, sent emails, signals and notifications. Your user accounts and sectors/countries are preserved.",
    start_wipe: "Start wipe procedure",
    confirm_title: "⚠ Irreversible action",
    confirm_bullet1: "All companies and their contacts will be deleted",
    confirm_bullet2: "Email history, notifications and AI signals will be erased",
    confirm_bullet3: "Your user accounts, sectors and countries stay intact",
    type_to_confirm: "To confirm, type exactly: {{token}}",
    confirm_wipe: "Confirm wipe",
    wiping: "Wiping in progress…",
    wipe_ok: "Data wiped ({{n}} documents) — you can now start fresh",
    popup_blocked: "Pop-up blocked — allow pop-ups for this site and retry.",
    popup_hint: "The download will start in this tab… don't close it.",
    export_triggered: "Download triggered in a new tab — check your Downloads folder.",
    token_not_ready: "Preparing secure link… retry in 2 seconds.",
    token_loading: "Preparing secure link…",
    fallback_title: "Download backup ZIP",
    fallback_hint: "The Emergent editor blocks direct downloads from the iframe. Copy the URL below and paste it in a new Chrome tab:",
    fallback_step1: "Click \"Copy\" below",
    fallback_step2: "Open a new Chrome tab (Ctrl+T) — NOT inside the editor",
    fallback_step3: "Paste the URL (Ctrl+V) then Enter — the ZIP downloads",
    token_expires_hint: "Link expires in 5 min — auto-refreshed every 4 min.",
    copy: "Copy",
    url_copied: "URL copied — paste into a new Chrome tab",
  },
  resend_domain: {
    title: "Sender domain (Resend)",
    subtitle: "Verify your domain to send digest to any address (by default, only your verified email receives tests).",
    current_sender: "Current sender address",
    test_mode: "Test mode",
    test_mode_hint: "In test mode, only addresses verified on your Resend account receive emails. Verify a domain to send to everyone.",
    add_domain: "Add a domain",
    add: "Add",
    added: "Domain added — configure DNS records below",
    existed: "Domain already on Resend",
    your_domains: "Your domains",
    check: "Check",
    verify_triggered: "Verification triggered",
    dns_records_title: "DNS records to add at your registrar",
    dns_hint: "Copy these 3 records at Cloudflare/OVH/Namecheap, then click « Check ».",
    use_as_sender: "Use as sender",
    sender_set: "Sender set: {{email}}",
    open_dashboard: "Open Resend dashboard",
  },
  mandant_dna: {
    title: "Mandant DNA — {{name}}",
    intro: "AI will deeply read the Mandant's website and understand their real business: products/services, target industries, buyer profiles, competitors.",
    intro_step1: "Auto-discovery of up to 10 key pages (homepage, products, solutions, references)",
    intro_step2: "Content extraction via Tavily and analysis by Claude (Anthropic)",
    intro_step3: "Structured profile you review before applying to the Mandant",
    website_label: "Website analyzed",
    no_website: "(no website on file)",
    warn_no_website: "Add a website to the Mandant first.",
    analyze_btn: "Run analysis",
    analyzing_title: "Analyzing…",
    analyzing_hint: "Crawl + AI — about 60 seconds for a full site.",
    meta_pages: "{{count}}/{{total}} pages read",
    section_pitch: "Pitch summary",
    section_products: "Products & services",
    section_industries: "End industries",
    section_sectors: "Suggested CRM sectors",
    section_buyers: "Buyer profile",
    section_competitors: "Identified competitors",
    section_keywords: "Prospecting keywords",
    section_redflags: "Caveats",
    buyer_size: "Company size",
    buyer_geo: "Geographic strength",
    buyer_roles: "Decision roles",
    buyer_use_cases: "Typical use cases",
    cancel: "Cancel",
    rerun: "Re-run",
    apply: "Apply to Mandant",
    applied: "Profile applied ({{fields}} fields, {{sectors}} sectors linked)",
    subsectors_created: "{{count}} new sub-sector(s) created",
    error_extract: "Could not extract content: {{error}}",
  },
  market_intel: {
    title: "Market Intelligence",
    subtitle: "Country-level strategic brief: projects, buyers, competitors, coverage gaps for new Mandant acquisition.",
    country: "Target country", pick_country: "Pick a country…",
    sector_hint: "Sector (optional)", sector_placeholder: "e.g. Water & Desalination",
    generate: "Generate brief", refresh: "Refresh",
    analyzing: "Analyzing…",
    analyzing_hint: "6 web searches + AI synthesis — about 90 seconds",
    from_cache: "Recent brief loaded from cache (< 7 days)",
    generated: "Brief generated ✓",
    cached_label: "from cache",
    section_macro: "Macro overview",
    section_sectors: "Growing sectors",
    section_projects: "Mega-projects",
    section_buyers: "Top B2B buyers",
    section_competitors: "Active competitors in country",
    section_gaps: "Coverage gaps (Mandant opportunities)",
    section_tenders: "Upcoming tenders",
    gap_ideal: "Ideal Mandant profile",
    gap_pitch: "Pitch angle",
    generate_pitch_pdf: "Generate pitch deck",
  },
  pitch_deck: {
    title: "Generate pitch deck",
    candidate_name: "Candidate Mandant name",
    candidate_placeholder: "e.g. Wabag",
    candidate_url: "Website (optional)",
    start: "Generate",
    running: "Generating…",
    generating: "Claude is writing the 4 slides… ~90s",
    download_pdf: "Download PDF",
  },
  quality: {
    title: "Quality Audit",
    subtitle: "Spot unverified contacts and companies without a decision-maker. Run a bulk Hunter.io verification.",
    kpi_contacts_verified: "Verified contacts",
    kpi_contacts_unverified: "Unverified",
    kpi_contacts_no_email: "Without email",
    kpi_companies_no_dm: "Without DM",
    section_companies_no_dm: "Companies without decision-maker",
    actionable_count: "{{actionable}} actionable out of {{total}} (with website)",
    selected: "{{count}} selected",
    bulk_verify_via_hunter: "Verify via Hunter",
    bulk_max: "Maximum 50 companies per batch.",
    bulk_done: "Audit complete: {{contacts}} new contacts added across {{companies}} companies.",
    all_companies_have_dm: "Every company has at least one decision-maker. 🎉",
    all_contacts_verified: "All contacts are verified. 🎉",
    section_unverified_contacts: "Unverified contacts",
    no_dm: "No decision-maker",
    no_website: "No website",
    col_website: "Website",
    col_status: "Status",
    run_hunter: "Search decision-makers via Hunter.io",
    no_website_for_hunter: "Add a website to the company to run Hunter.io.",
    hunter_found: "{{count}} decision-maker(s) found via {{provider}} ✓",
    hunter_pattern_only: "No decision-maker found, but email pattern detected: {{pattern}}",
    hunter_none: "No decision-maker found via Hunter.io. Use LinkedIn to search manually.",
    tavily_cleanup_title: "Polluted web imports detected",
    tavily_cleanup_subtitle: "{{count}} companies imported via Tavily look like articles or directories, not real companies. Examined: {{examined}}.",
    tavily_cleanup_delete: "Delete ({{count}})",
    cleanup_confirm: "Permanently delete {{count}} polluted entries? This cannot be undone.",
    cleanup_done: "{{count}} polluted entries deleted ✓",
  },
  api_credits: {
    title: "API credits & quotas",
    subtitle: "Status of keys and remaining credits for every external service used by the app.",
    refresh: "Refresh",
    summary_title: "{{ok}} / {{total}} services operational",
    configured_count: "{{configured}} keys configured out of {{total}}",
    checked_at: "Checked at {{time}}",
    not_configured: "Not configured",
    ok: "OK",
    error: "Error",
    manage: "Manage key",
    searches_month: "Searches this month",
    verifications_month: "Verifications this month",
    paid_credits: "Paid credits",
    free_credits: "Free credits",
    reset_on: "Resets on {{date}}",
  },
  api_alerts: {
    view: "View",
    dismiss: "Dismiss",
    more_count: "+{{count}} other alert(s) on other services",
    toast_description: "Check API credits.",
    msg_warning_quota: "{{name}}: {{remaining}}/{{total}} requests left this month ({{pct}}%).",
    msg_warning_credits: "{{name}}: only {{remaining}} credits left.",
    msg_critical_quota: "{{name}}: critical — {{remaining}}/{{total}} requests ({{pct}}%).",
    msg_critical_credits: "{{name}}: critical — {{remaining}} credits left.",
    msg_over: "{{name}}: monthly quota exceeded ({{total}} requests). Service blocked until reset.",
    msg_error: "{{name}}: service error. {{error}}",
  },
};

export default en;
